/**
 * UserProfileMenu - User avatar button with profile/logout dropdown
 *
 * Renders the user avatar as an IconButton. Clicking it opens a Menu with
 * the user's name/email, an optional profile-settings link (dev only),
 * and a logout action.
 */

import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@auth0/auth0-react';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Box,
  Alert,
  Snackbar,
  type SnackbarCloseReason,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { getIconComponent } from '../../utils/icons';
import { useMountEffect } from '../../hooks/useMountEffect';
import {
  extractFullUserName,
  extractUserInitials,
  extractPlatformUser,
} from '../../utils/auth0UserUtils';
import { usePspContext } from '../../contexts/PspContext';
import { useEntity } from '../../contexts/EntityContext';
import { RouteRegistryId } from '../../types';
import { TestIds } from '../../constants';
import { useLoggerWithContext } from '../../services/logger/loggerContext';

/** How long a requested Auth0 redirect may take to leave the page before "Log out" is offered again. */
const LOGOUT_REDIRECT_GRACE_MS = 10_000;

/** Shown in the open menu and in the snackbar, whichever reports the failure. */
const LOGOUT_FAILED_MESSAGE = "Couldn't log out. Try again.";

const CloseIcon = getIconComponent('Close');

interface UserProfileMenuProps {
  user: User | undefined;
  /** Settles once sign-out cleanup is done and the Auth0 redirect has started. */
  onLogout: () => void | Promise<void>;
}

export function UserProfileMenu({ user, onLogout }: UserProfileMenuProps) {
  const { routeRegistry } = usePspContext();
  const { activeEntity } = useEntity();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const menuId = useId();
  const [signingOut, setSigningOut] = useState(false);
  const [logoutFailed, setLogoutFailed] = useState(false);
  const redirectGraceTimerRef = useRef<number | undefined>(undefined);
  const logger = useLoggerWithContext();

  useMountEffect(() => () => window.clearTimeout(redirectGraceTimerRef.current));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setLogoutFailed(false);
  };

  const handleProfileSettings = () => {
    // Get platform user ID for profile URL
    const platformUser = extractPlatformUser(user);

    // Find the access management route
    const accessRoute = routeRegistry.find((r) => r.id === RouteRegistryId.ACCESS_MANAGEMENT);

    if (accessRoute && activeEntity?.friendlyId && platformUser?.id) {
      handleClose();
      // Navigate to /access/{entity}/users/{userId}
      navigate(`${accessRoute.basePath}/${activeEntity.friendlyId}/users/${platformUser.id}`);
    }
  };

  // Sign-out waits for modules to end their sessions before the Auth0 redirect, which can take a
  // couple of seconds. Showing that, and refusing a second click, beats a menu that just closes.
  // Success stays pending while the page navigates away, unless the navigation never happens.
  const handleLogout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setLogoutFailed(false);
    try {
      await onLogout();
      redirectGraceTimerRef.current = window.setTimeout(
        () => setSigningOut(false),
        LOGOUT_REDIRECT_GRACE_MS
      );
    } catch (err) {
      logger.error('Sign-out failed', {
        auth_event: 'logout_error',
        error_message: err instanceof Error ? err.message : String(err),
      });
      setSigningOut(false);
      setLogoutFailed(true);
    }
  };

  const dismissLogoutFailed = (_event?: unknown, reason?: SnackbarCloseReason) => {
    if (reason !== 'clickaway') setLogoutFailed(false);
  };

  const displayName = extractFullUserName(user) || 'User';

  return (
    <>
      <IconButton
        data-testid={TestIds.USER_MENU_BUTTON}
        onClick={handleOpen}
        aria-label={displayName}
        aria-controls={menuOpen ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? 'true' : undefined}
        size="large"
        sx={{ p: 0, width: (theme) => theme.spacing(6), height: (theme) => theme.spacing(6) }}
      >
        <Avatar
          src={user?.picture}
          alt={displayName}
          sx={{
            width: (theme) => theme.spacing(5),
            height: (theme) => theme.spacing(5),
            bgcolor: 'primary.main',
            fontSize: (theme) => theme.typography.body2.fontSize,
            fontWeight: 600,
          }}
        >
          {extractUserInitials(user)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ mt: 1 }}
        slotProps={{ list: { id: menuId } }}
      >
        <Box component="li" role="none" sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {displayName}
          </Typography>
          {user?.email && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider component="li" />
        <MenuItem
          data-testid={TestIds.PROFILE_SETTINGS_BUTTON}
          onClick={handleProfileSettings}
          disabled={signingOut}
        >
          <Typography variant="body2">Profile Settings</Typography>
        </MenuItem>
        <MenuItem data-testid={TestIds.LOGOUT_BUTTON} onClick={handleLogout} disabled={signingOut}>
          <Typography variant="body2">{signingOut ? 'Logging out…' : 'Log out'}</Typography>
        </MenuItem>
        {/* Inside the menu: while it is open, the modal hides the rest of the page from screen
            readers. A presentational item, since a menu may only hold menu items directly. */}
        <Box component="li" role="none">
          {logoutFailed && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
              {LOGOUT_FAILED_MESSAGE}
            </Alert>
          )}
          <Box component="output" data-testid={TestIds.LOGOUT_STATUS} sx={visuallyHidden}>
            {signingOut ? 'Logging out' : ''}
          </Box>
        </Box>
      </Menu>
      {/* A failure while the menu is closed is reported here instead of reopening the menu,
          which would take focus from wherever the user moved it. Unmounted while the menu is open,
          which moves the failure into the menu: the open menu would hide it from screen readers,
          and an exit transition would leave a second alert beside the menu's. */}
      {!menuOpen && (
        <Snackbar open={logoutFailed} onClose={dismissLogoutFailed}>
          <Alert
            severity="error"
            action={
              <IconButton
                data-testid={TestIds.LOGOUT_FAILED_DISMISS}
                aria-label="Dismiss log-out error"
                color="inherit"
                size="small"
                onClick={dismissLogoutFailed}
              >
                <CloseIcon aria-hidden />
              </IconButton>
            }
          >
            {LOGOUT_FAILED_MESSAGE}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}
